// Import React directly to ensure it's available
import React from 'react';
// Import hooks individually
import { useState, useEffect, ReactNode } from 'react';

// Log React availability for debugging
console.log('[useAuth] React availability check:', {
  reactAvailable: typeof React !== 'undefined',
  createContextAvailable: typeof React.createContext === 'function'
});

// Ensure React is available for context creation
if (!React || typeof React.createContext !== 'function') {
  console.error('[useAuth] CRITICAL ERROR: React.createContext is not available');
  throw new Error('React.createContext is not available. Authentication cannot be initialized.');
}

import { supabase } from "@/integrations/supabase/client";
import { User, Session } from "@supabase/supabase-js";
import { ExtendedProfile } from "@/types/database";

interface AuthContextType {
  user: User | null;
  profile: ExtendedProfile | null;
  session: Session | null;
  login: (email: string, password: string) => Promise<boolean>;
  signup: (email: string, password: string, userData: any) => Promise<boolean>;
  signInWithGoogle: () => Promise<boolean>;
  logout: () => Promise<void>;
  isLoading: boolean;
  refreshProfile: () => Promise<void>;
  setDemoRole: (role: 'patient' | 'doctor') => void;
}

// Create auth context using React.createContext
const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

// Demo mode - set to true to bypass authentication
const DEMO_MODE = true;

// Mock demo data
const demoUser = {
  id: 'demo-user-123',
  email: 'demo@clinicaflow.com',
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  email_confirmed_at: new Date().toISOString(),
} as User;

const demoProfile = {
  id: 'demo-user-123',
  email: 'demo@clinicaflow.com',
  first_name: 'Demo',
  last_name: 'User',
  role: 'patient', // Can be changed to 'doctor', 'admin', 'staff'
  phone: '+1-555-0123',
  clinic_id: 'demo-clinic-123',
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
} as ExtendedProfile;

const demoSession = {
  access_token: 'demo-token',
  refresh_token: 'demo-refresh',
  expires_in: 3600,
  token_type: 'bearer',
  user: demoUser,
} as Session;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(DEMO_MODE ? demoUser : null);
  const [profile, setProfile] = useState<ExtendedProfile | null>(DEMO_MODE ? demoProfile : null);
  const [session, setSession] = useState<Session | null>(DEMO_MODE ? demoSession : null);
  const [isLoading, setIsLoading] = useState(DEMO_MODE ? false : true);

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 is "not found"
        console.error('Error fetching profile:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error fetching profile:', error);
      return null;
    }
  };

  const refreshProfile = async () => {
    if (user) {
      const profileData = await fetchProfile(user.id);
      setProfile(profileData);
    }
  };

  const setDemoRole = (role: 'patient' | 'doctor') => {
    if (DEMO_MODE) {
      const updatedProfile = {
        ...demoProfile,
        role: role,
        first_name: role === 'doctor' ? 'Dr. Demo' : 'Demo',
        last_name: role === 'doctor' ? 'Physician' : 'Patient',
      };
      setProfile(updatedProfile);
      console.log(`Demo role set to: ${role}`);
    }
  };

  useEffect(() => {
    // Skip Supabase auth setup in demo mode
    if (DEMO_MODE) {
      console.log('Demo mode enabled - skipping Supabase auth setup');
      return;
    }

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, session?.user?.email);
        
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Fetch profile data after authentication
          setTimeout(async () => {
            const profileData = await fetchProfile(session.user.id);
            setProfile(profileData);
            setIsLoading(false);
          }, 0);
        } else {
          setProfile(null);
          setIsLoading(false);
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        fetchProfile(session.user.id).then((profileData) => {
          setProfile(profileData);
          setIsLoading(false);
        });
      } else {
        setIsLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    if (DEMO_MODE) {
      console.log('Demo mode login - accepting any credentials');
      // In demo mode, just return true for any credentials
      return true;
    }

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('Login error:', error);
        return false;
      }

      return !!data.user;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const signInWithGoogle = async (): Promise<boolean> => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin,
        },
      });

      if (error) {
        console.error('Google sign-in error:', error);
        return false;
      }

      // OAuth redirects, so we return true to indicate the process started
      return true;
    } catch (error) {
      console.error('Google sign-in error:', error);
      return false;
    }
  };

  const signup = async (email: string, password: string, userData: any): Promise<boolean> => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: userData.firstName,
            last_name: userData.lastName,
            role: userData.role || 'patient',
            phone: userData.phone,
          },
        },
      });

      if (error) {
        console.error('Signup error:', error);
        return false;
      }

      return !!data.user;
    } catch (error) {
      console.error('Signup error:', error);
      return false;
    }
  };

  const logout = async () => {
    try {
      // Sign out from all devices and sessions
      await supabase.auth.signOut({ scope: 'global' });
      // Clear all state
      setUser(null);
      setProfile(null);
      setSession(null);
      // Force clear any localStorage remnants
      localStorage.removeItem('supabase.auth.token');
      sessionStorage.setItem('force_auth_reset', 'true');
    } catch (error) {
      console.error('Logout error:', error);
      // Still try to reset state even if the API call fails
      setUser(null);
      setProfile(null);
      setSession(null);
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      profile,
      session,
      login,
      signup,
      signInWithGoogle,
      logout,
      isLoading,
      refreshProfile,
      setDemoRole,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook to use auth context
export function useAuth() {
  // Use React.useContext directly to avoid import issues
  const context = React.useContext(AuthContext);
  
  // Log context availability for debugging
  console.log('[useAuth] Context check:', {
    contextAvailable: context !== undefined,
    authContextExists: AuthContext !== undefined
  });
  
  if (context === undefined) {
    console.error('[useAuth] Context is undefined. AuthProvider may not be properly initialized.');
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
